import React, { useEffect, useState } from "react";
import api, { formatIDR, formatDate } from "@/lib/api";
import { PartnerCombobox } from "@/components/PartnerCombobox";
import { calcTotals } from "@/components/TaxRegsPanel";
import { toast } from "sonner";
import { usePaginated } from "@/components/Paginator";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MoneyInput, PercentInput } from "@/components/ui/money-input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash, PencilSimple, PaperPlaneTilt, Play, Repeat, ArrowCounterClockwise } from "@phosphor-icons/react";

const emptyItem = () => ({ product_id: "", product_name: "", item_type: "product", description: "", quantity: 1, unit: "pcs", price: 0, discount: 0, discount_percent: 0, tax_rate: 0.11, pph23_rate: 0, revenue_account_code: "4000" });
const emptyForm = () => ({ name: "", partner_id: "", partner_name: "", day_of_month: 1, due_days: 30, discount_global: 0, items: [emptyItem()], notes: "", auto_send: false, sender_user_id: "", attachments: ["invoice_pdf"], active: true, start_date: "", end_date: "" });

export default function RecurringInvoices() {
  const [list, setList] = useState([]);
  const [partners, setPartners] = useState([]);
  const [products, setProducts] = useState([]);
  const [senders, setSenders] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const load = async () => {
    const [l, p, pr, s] = await Promise.all([
      api.get("/recurring-invoices"),
      api.get("/partners", { params: { type: "customer" } }),
      api.get("/inventory/products"),
      api.get("/reminders/senders").catch(() => ({ data: [] })),
    ]);
    setList(l.data); setPartners(p.data); setProducts(pr.data); setSenders(s.data);
  };
  useEffect(() => { load(); }, []);
  const pg = usePaginated(list);

  const openNew = () => { setForm(emptyForm()); setDlg("new"); };
  const openEdit = (tpl) => {
    setForm({ ...emptyForm(), ...tpl, items: (tpl.items || []).map(i => ({ ...emptyItem(), ...i })) });
    setDlg(tpl.id);
  };
  const submit = async () => {
    if (!form.name || !form.partner_id) return toast.error("Nama template & pelanggan wajib");
    if ((form.items || []).length === 0) return toast.error("Minimal 1 item");
    setSaving(true);
    try {
      const items = form.items.map(i => ({ ...i, quantity: Number(i.quantity), price: Number(i.price), discount: Number(i.discount || 0), discount_percent: Number(i.discount_percent || 0), tax_rate: Number(i.tax_rate || 0), pph23_rate: Number(i.pph23_rate || 0) }));
      const payload = { ...form, day_of_month: Number(form.day_of_month), due_days: Number(form.due_days), discount_global: Number(form.discount_global || 0), items };
      if (dlg === "new") await api.post("/recurring-invoices", payload);
      else await api.put(`/recurring-invoices/${dlg}`, payload);
      toast.success("Template tersimpan"); setDlg(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal simpan"); }
    finally { setSaving(false); }
  };
  const del = async (tpl) => {
    if (!window.confirm(`Hapus template "${tpl.name}"?`)) return;
    try { await api.delete(`/recurring-invoices/${tpl.id}`); toast.success("Terhapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const runNow = async (tpl) => {
    if (!window.confirm(`Terbitkan faktur SEKARANG dari template "${tpl.name}"? Ini akan menambah faktur baru & jurnal.`)) return;
    try {
      const r = await api.post(`/recurring-invoices/${tpl.id}/run-now`);
      const em = r.data.email;
      toast.success(`Faktur ${r.data.invoice_number} dibuat${em?.sent ? ` & dikirim via ${em.channel} ke ${em.to}` : ` (email: ${em?.reason || "off"})`}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const setItem = (idx, patch) => { const a = [...form.items]; a[idx] = { ...a[idx], ...patch }; setForm({ ...form, items: a }); };
  const readySenders = senders.filter(s => s.channel);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl" data-testid="recurring-page">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div>
          <h1 className="font-serif-display text-4xl text-slate-900 flex items-center gap-2"><Repeat size={32} weight="duotone" className="text-amber-600" />Faktur Berulang</h1>
          <p className="text-sm text-slate-500 mt-1">Terbit otomatis tiap bulan (jam 07:00 WIB) ke pelanggan yang sama, dengan opsi kirim email otomatis.</p>
        </div>
        <Button onClick={openNew} className="bg-amber-600 hover:bg-amber-700" data-testid="rec-new-btn"><Plus size={16} className="mr-1" />Template Baru</Button>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nama Template</TableHead><TableHead>Pelanggan</TableHead><TableHead>Terbit Tgl</TableHead>
              <TableHead>Berikutnya</TableHead><TableHead>Terakhir</TableHead><TableHead>Auto-Kirim</TableHead>
              <TableHead>Status</TableHead><TableHead className="w-40"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-8">Belum ada template. Klik "Template Baru".</TableCell></TableRow>}
            {pg.paged.map(t => (
              <TableRow key={t.id} data-testid={`rec-row-${t.id}`}>
                <TableCell className="font-medium">{t.name}</TableCell>
                <TableCell>{t.partner_name}</TableCell>
                <TableCell>Tanggal {t.day_of_month}</TableCell>
                <TableCell className="text-amber-700 font-medium" data-testid={`rec-next-${t.id}`}>{formatDate(t.next_run_date)}</TableCell>
                <TableCell>{t.last_invoice_number ? <span className="text-xs">{t.last_invoice_number}<div className="text-slate-400">{formatDate(t.last_run_date)} · {t.runs}x</div></span> : <span className="text-slate-400 text-xs">-</span>}</TableCell>
                <TableCell>{t.auto_send ? <Badge className="bg-emerald-100 text-emerald-800">Ya</Badge> : <Badge variant="outline">Tidak</Badge>}</TableCell>
                <TableCell>{t.active ? <Badge className="bg-blue-100 text-blue-800">Aktif</Badge> : <Badge variant="outline">Nonaktif</Badge>}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => runNow(t)} className="border-amber-300 text-amber-700 hover:bg-amber-50" title="Jalankan sekarang" data-testid={`rec-run-${t.id}`}><Play size={12} weight="fill" /></Button>
                    <Button size="sm" variant="outline" onClick={() => openEdit(t)} data-testid={`rec-edit-${t.id}`}><PencilSimple size={12} /></Button>
                    <Button size="sm" variant="outline" onClick={() => del(t)} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`rec-del-${t.id}`}><Trash size={12} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {pg.pager}
      </Card>

      <Dialog open={!!dlg} onOpenChange={(o) => { if (!o) setDlg(null); }}>
        <DialogContent className="w-[95vw] max-w-5xl max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0">
          <DialogHeader className="shrink-0 border-b border-slate-200 px-6 pt-6 pb-3"><DialogTitle>{dlg === "new" ? "Template Faktur Berulang" : "Edit Template"}</DialogTitle></DialogHeader>
          <div className="space-y-3 min-h-0 overflow-y-auto px-6 pt-4 pb-6">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nama Template</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Sewa Bulanan Ruko A" data-testid="rec-name-input" /></div>
              <div><Label>Pelanggan</Label>
                <PartnerCombobox partners={partners} value={form.partner_id} testid="rec-partner-select" onChange={(p) => setForm({ ...form, partner_id: p.id, partner_name: p?.name || "" })} />
              </div>
              <div><Label>Terbit Setiap Tanggal (1-31)</Label><Input type="number" min={1} max={31} value={form.day_of_month} onChange={(e) => setForm({ ...form, day_of_month: e.target.value })} data-testid="rec-dom-input" /></div>
              <div><Label>Jatuh Tempo (hari setelah terbit)</Label><Input type="number" min={0} value={form.due_days} onChange={(e) => setForm({ ...form, due_days: e.target.value })} data-testid="rec-due-days-input" /></div>
              <div><Label>Mulai (opsional)</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="rec-start-input" /></div>
              <div><Label>Berakhir (opsional)</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="rec-end-input" /></div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1"><Label>Item</Label>
                <Button size="sm" variant="outline" onClick={() => setForm({ ...form, items: [...form.items, emptyItem()] })} data-testid="rec-add-item"><Plus size={12} className="mr-1" />Baris</Button>
              </div>
              <div className="border border-slate-200 rounded p-2 space-y-2">
                <div className="grid grid-cols-12 gap-2 text-[11px] uppercase tracking-wider text-slate-500" data-testid="rec-items-header">
                  <span className="col-span-3">Produk / Jasa</span>
                  <span className="col-span-1">Qty</span>
                  <span className="col-span-3">Harga Satuan (Rp)</span>
                  <span className="col-span-2">Diskon % / Rp</span>
                  <span className="col-span-2">PPN %</span>
                  <span className="col-span-1"></span>
                </div>
                {form.items.map((it, idx) => (
                  <React.Fragment key={idx}>
                    <div className="grid grid-cols-12 gap-2 items-center" data-testid={`rec-item-${idx}`}>
                      <Select value={it.product_id} onValueChange={(v) => { const p = products.find(x => x.id === v); setItem(idx, { product_id: v, product_name: p?.name || "", item_type: p?.item_type || "product", description: p?.description || "", price: p?.sale_price || 0, unit: p?.base_unit || "pcs" }); }}>
                        <SelectTrigger className="col-span-3" data-testid={`rec-item-prod-${idx}`}><SelectValue placeholder="Pilih produk/jasa" /></SelectTrigger>
                        <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.name} {p.item_type === "service" ? "(Jasa)" : ""}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input className="col-span-1" type="number" placeholder="Qty" value={it.quantity} onChange={(e) => setItem(idx, { quantity: e.target.value })} />
                      <MoneyInput className="col-span-3" placeholder="Harga" value={it.price} onChange={(e) => setItem(idx, { price: e.target.value })} data-testid={`rec-item-price-${idx}`} />
                      <div className="col-span-2 flex gap-1">
                        <Input className="w-14 shrink-0" type="number" min="0" max="100" step="0.01" placeholder="%" value={it.discount_percent || 0} onChange={(e) => { const pct = Number(e.target.value) || 0; const rp = Math.round((Number(it.quantity) || 0) * (Number(it.price) || 0) * pct / 100); setItem(idx, { discount_percent: e.target.value, discount: rp }); }} data-testid={`rec-item-discount-pct-${idx}`} title="Diskon persen — otomatis dikonversi ke Rp" />
                        <MoneyInput className="flex-1 min-w-0" placeholder="Rp" value={it.discount || 0} onChange={(e) => { const rp = Number(e.target.value) || 0; const base = (Number(it.quantity) || 0) * (Number(it.price) || 0); setItem(idx, { discount: e.target.value, discount_percent: base > 0 ? +(rp / base * 100).toFixed(2) : 0 }); }} data-testid={`rec-item-discount-${idx}`} title="Diskon Rp" />
                      </div>
                      <PercentInput className="col-span-2" placeholder="PPN %" value={it.tax_rate} onChange={(e) => setItem(idx, { tax_rate: e.target.value })} />
                      <Button size="sm" variant="outline" onClick={() => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) })} className="col-span-1 border-rose-300 text-rose-700"><Trash size={12} /></Button>
                    </div>
                    <div className="grid grid-cols-12 gap-2 items-start">
                      <Textarea className="col-span-11 text-xs min-h-[52px]" rows={2} placeholder="Deskripsi item (opsional) — muncul di baris item PDF/faktur. Bisa multi-baris (Enter) dan boleh beda dari data master."
                        value={it.description || ""} onChange={(e) => setItem(idx, { description: e.target.value })}
                        data-testid={`rec-item-description-${idx}`} />
                      {it.product_id ? (
                        <button type="button" className="col-span-1 mt-1 text-slate-400 hover:text-amber-600" title="Reset ke deskripsi master"
                          onClick={() => { const p = products.find(x => x.id === it.product_id); setItem(idx, { description: p?.description || "" }); }}
                          data-testid={`rec-item-desc-reset-${idx}`}>
                          <ArrowCounterClockwise size={14} />
                        </button>
                      ) : <span className="col-span-1" />}
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3 justify-end">
              <Label className="whitespace-nowrap text-slate-600">Diskon Dokumen (Rp)</Label>
              <MoneyInput className="w-44" placeholder="0" value={form.discount_global || 0} onChange={(e) => setForm({ ...form, discount_global: e.target.value })} data-testid="rec-discount-global" />
            </div>

            {(() => {
              const t = calcTotals(form.items || [], 0.11, Number(form.discount_global) || 0);
              return (
                <div className="bg-slate-50 border border-slate-200 rounded-md p-3 space-y-1 text-sm" data-testid="rec-totals-summary">
                  <div className="flex justify-between"><span className="text-slate-600">Subtotal (DPP)</span><span className="tabular-nums">{formatIDR(t.sub)}</span></div>
                  {Number(form.discount_global) > 0 && <div className="flex justify-between text-rose-600" data-testid="rec-discount-global-row"><span>Diskon Dokumen (sudah termasuk di DPP)</span><span className="tabular-nums">-{formatIDR(Number(form.discount_global))}</span></div>}
                  <div className="flex justify-between"><span className="text-slate-600">PPN</span><span className="tabular-nums">{formatIDR(t.tax)}</span></div>
                  <div className="flex justify-between font-semibold border-t border-slate-200 pt-1"><span>Total Faktur</span><span className="tabular-nums" data-testid="rec-total">{formatIDR(t.total)}</span></div>
                </div>
              );
            })()}

            <div><Label>Catatan (opsional)</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="rec-notes-input" /></div>

            <div className="border border-amber-200 bg-amber-50 rounded-md p-3 space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                <input type="checkbox" checked={form.auto_send} onChange={(e) => setForm({ ...form, auto_send: e.target.checked })} data-testid="rec-autosend-check" />
                <PaperPlaneTilt size={16} weight="duotone" className="text-amber-600" />
                Kirim otomatis via email ke pelanggan setelah faktur diterbitkan
              </label>
              {form.auto_send && (
                <>
                  <div><Label className="text-xs">User Pengirim (SMTP/Gmail terhubung)</Label>
                    <Select value={form.sender_user_id} onValueChange={(v) => setForm({ ...form, sender_user_id: v })}>
                      <SelectTrigger data-testid="rec-sender-select"><SelectValue placeholder={readySenders.length ? "Pilih user" : "Belum ada user terhubung"} /></SelectTrigger>
                      <SelectContent>{readySenders.map(s => <SelectItem key={s.id} value={s.id}>{s.name || s.email} — {s.channel === "smtp" ? "SMTP" : "Gmail"}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Lampiran</Label>
                    <div className="space-y-1 mt-1">
                      {[["invoice_pdf", "Faktur PDF"], ["kwitansi_pdf", "Kwitansi (jika sudah lunas)"], ["profile_pdf", "Profil Perusahaan"]].map(([k, lbl]) => (
                        <label key={k} className="flex items-center gap-2 text-sm">
                          <input type="checkbox" checked={form.attachments.includes(k)} onChange={(e) => setForm({ ...form, attachments: e.target.checked ? [...form.attachments, k] : form.attachments.filter(x => x !== k) })} data-testid={`rec-att-${k}`} />
                          {lbl}
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="rec-active-check" />
              Aktif — akan diterbitkan otomatis oleh cron
            </label>

            <div className="sticky bottom-0 z-10 -mx-6 -mb-6 mt-2 border-t border-slate-200 bg-background px-6 py-3">
              <Button onClick={submit} disabled={saving} className="w-full bg-slate-900" data-testid="rec-save-btn">{saving ? "Menyimpan..." : "Simpan Template"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
